import { useContext, useEffect, useState } from "react";
import { AuthContext } from "context/AuthContext";
import {
  SvgContactUser,
  SvgContactPhone,
  SvgContactEmail,
  SvgShare,
} from "ui/svgs";
import { shareAlert } from "lib/actions";
import { SiteDetailRes } from "lib/types";
import clsx from "clsx";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin, Tooltip } from "antd";
import { getSiteDetail } from "pages/dashboard/cases/service";
import { showToast } from "ui/components/common";
import { parsedPhoneNumber, userRoleToDisplay } from "lib/utils";
import { shareCase } from "pages/dashboard/cases/service";
import { USER_ROLE } from "lib/constants";
type PropsType = {
  siteId: string;
  maxHeight?: number;
  id: string;
  type: string; // alert or case
};
export default function Contacts({ siteId, maxHeight, id, type }: PropsType) {
  const { user } = useContext(AuthContext);
  const [siteData, setSite] = useState<SiteDetailRes>();
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (!siteId) {
      return;
    }
    const init = async () => {
      setLoading(true);
      const withCustomer =
        user.role === USER_ROLE.Operator ||
        user.role === USER_ROLE.Technician ||
        user.role === USER_ROLE.Dispatcher ||
        user.role === USER_ROLE.ProjectManager;
      const res = await getSiteDetail(user.orgId, +siteId, withCustomer);
      setLoading(false);
      if (res.status != 200) {
        setSite(null);
        return;
      }
      setSite(res?.data);
    };
    init();
  }, [siteId]);

  const handleShare = async (phone: string, email?: string) => {
    try {
      const phoneNumberArray = phone ? [phone] : [];
      const emailArray = email ? [email] : [];
      if (type === "alert") {
        await shareAlert(
          user.token,
          id,
          emailArray,
          phoneNumberArray,
          24 * 60 * 3,
          "",
        );
      } else {
        await shareCase(
          id,
          emailArray,
          phoneNumberArray,
          24 * 60 * 3,
          "",
          user.orgId,
        );
      }

      showToast(0, `The ${type} has been shared successfully.`);
    } catch (error) {
      console.error(`Error sharing  ${type} :`, error);
      showToast(
        1,
        `There was an error sharing the ${type}. Please try again later.`,
      );
    }
  };

  const tooltipTitle = type === "alert" ? "Share Alert" : "Share Case";

  const customerAndMangers = siteData
    ? [siteData?.customer, ...siteData.managers]
    : [];

  return (
    <div
      className="px-4 w-full text-base fill-neutral-400 overflow-y-auto"
      style={{ maxHeight: maxHeight }}
    >
      {isLoading ? (
        <div className="w-full h-24 flex items-center justify-center">
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </div>
      ) : (
        <>
          {customerAndMangers?.map((manager) => {
            if (!manager) {
              return;
            }
            const parsedNumber = parsedPhoneNumber(manager?.phoneNumber);
            return (
              <div
                className={clsx(
                  "flex w-full items-center text-neutral-800 py-3 border-t",
                )}
                key={manager.id}
              >
                <p className="w-1/3 font-semibold ">
                  {userRoleToDisplay(manager?.role?.name as USER_ROLE)}
                </p>
                <div className="fill-neutral-400 w-1/3">
                  <div className="flex gap-2 items-center">
                    <SvgContactUser width="24" className="flex-shrink-0" />
                    <p>
                      {manager?.firstName} {manager?.lastName}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-1 mt-2 items-center">
                    <SvgContactPhone width="18" className="flex-shrink-0" />
                    <p>{parsedNumber || "-"}</p>
                    {manager?.phoneNumber && (
                      <Tooltip title={tooltipTitle}>
                        <button
                          className="bg-[#DCE7FD] rounded w-[22px] h-[22px] flex items-center justify-center disabled:hover:bg-transparent fill-[#3E83F6] disabled:fill-[#717171] disabled:bg-transparent"
                          disabled={!manager?.phoneNumber}
                          onClick={() => handleShare(manager?.phoneNumber)}
                        >
                          <SvgShare width="17" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>
                <div className="w-1/3 flex gap-2 self-start items-center">
                  <SvgContactEmail
                    width="16"
                    className="flex-shrink-0 relative"
                  />
                  <p>{manager?.email}</p>
                  {manager?.email && (
                    <Tooltip title={tooltipTitle}>
                      <button
                        className="bg-[#DCE7FD] rounded w-[22px] h-[22px] flex items-center justify-center disabled:hover:bg-transparent fill-[#3E83F6] disabled:fill-[#717171] disabled:bg-transparent"
                        onClick={() => handleShare("", manager?.email)}
                      >
                        <SvgShare width="17" />
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            );
          })}
          {
            <div className="flex w-full  items-center text-neutral-800 py-3">
              <p className="w-1/3  font-semibold ">PD Station</p>
              <div className="w-1/3">
                <div className="flex gap-2 ">
                  <SvgContactUser width="24" />{" "}
                  <p>{siteData?.data?.pdStation || "-"}</p>
                </div>
                <div className="flex gap-2 ml-1 items-center mt-2">
                  <SvgContactPhone width="18" className="flex-shrink-0" />
                  <p>
                    {parsedPhoneNumber(siteData?.data?.officerPhone) || "-"}
                  </p>
                  {siteData?.data?.officerPhone && (
                    <Tooltip title={tooltipTitle}>
                      <button
                        className="hover:bg-[#EEEEEE] rounded-full w-[22px] h-[22px] flex items-center justify-center disabled:hover:bg-transparent"
                        disabled={!siteData?.data?.officerPhone}
                        onClick={() =>
                          handleShare(siteData?.data?.officerPhone)
                        }
                      >
                        <SvgShare width="17" className="fill-[#717171]" />
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
              <div className="w-1/3" />
            </div>
          }
          {
            <div className="flex w-full  items-center text-neutral-800 py-3">
              <p className="w-1/3  font-semibold ">Patrol Guard</p>
              <div className="w-1/3">
                <div className="flex gap-2 ">
                  <SvgContactUser width="24" />
                  <p>{siteData?.data?.patrolGuardName || "-"}</p>
                </div>
                <div className="flex gap-2 ml-1 items-center mt-2">
                  <SvgContactPhone width="18" className="flex-shrink-0" />
                  <p>
                    {parsedPhoneNumber(siteData?.data?.patrolGuardPhone) || "-"}
                  </p>
                  {siteData?.data?.patrolGuardPhone && (
                    <Tooltip title={tooltipTitle}>
                      <button
                        className="bg-[#DCE7FD] rounded w-[22px] h-[22px] flex items-center justify-center disabled:hover:bg-transparent fill-[#3E83F6] disabled:fill-[#717171] disabled:bg-transparent"
                        disabled={!siteData?.data?.patrolGuardPhone}
                        onClick={() =>
                          handleShare(siteData?.data?.patrolGuardPhone)
                        }
                      >
                        <SvgShare width="17" className="" />
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
              <div className="w-1/3" />
            </div>
          }
        </>
      )}
    </div>
  );
}
